import { useId, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { Alert, ErrorAlert } from '../components/ui'

/**
 * « Autoriser Claude » (connexion OAuth d'un assistant, décision § 14) : la personne, connectée, choisit le foyer
 * et l'accès. L'accord crée un jeton d'accès du foyer (Réglages › Jetons d'accès, révocable) ; on revient ensuite
 * vers l'assistant.
 */
export function OAuthAuthorizePage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const { households, current } = useHousehold()
  const groupHousehold = useId()
  const groupScope = useId()
  const clientId = params.get('client_id') ?? ''
  const redirectUri = params.get('redirect_uri') ?? ''
  const codeChallenge = params.get('code_challenge') ?? ''
  const valid = params.get('response_type') === 'code' && params.get('code_challenge_method') === 'S256' && Boolean(clientId && redirectUri && codeChallenge)
  const client = useQuery({
    queryKey: ['oauth-client', clientId, redirectUri],
    enabled: valid,
    retry: false,
    queryFn: () => api.get<{ clientId: string; name: string | null }>(`/oauth/client?${new URLSearchParams({ clientId, redirectUri })}`),
  })
  const writable = households.filter((h) => h.role !== 'viewer' && !h.readOnly)
  const [householdId, setHouseholdId] = useState<string | null>(null)
  const chosen = householdId ?? (writable.some((h) => h.id === current?.id) ? current!.id : writable[0]?.id) ?? null
  const [scope, setScope] = useState<'read' | 'read_write'>('read_write')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const decide = async (decision: 'allow' | 'deny') => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.post<{ redirectTo: string }>('/oauth/authorize', {
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: 'S256',
        state: params.get('state') ?? undefined,
        decision,
        ...(decision === 'allow' ? { householdId: chosen, scope } : {}),
      })
      window.location.assign(res.redirectTo)
    } catch (e) {
      setError(e)
      setBusy(false)
    }
  }

  if (!valid) return <Alert kind="error">{t('oauth.invalidRequest')}</Alert>
  const name = client.data?.name || t('oauth.defaultClient')
  return (
    <div className="stack" style={{ maxWidth: '36rem' }}>
      <h1>{t('oauth.title', { client: name })}</h1>
      <ErrorAlert error={client.error ?? error} />
      {client.data && (
        <>
          <p>{t('oauth.intro', { client: name })}</p>
          {writable.length === 0 ? (
            <Alert kind="warning">{t('oauth.noHousehold')}</Alert>
          ) : (
            <>
              {writable.length > 1 && (
                <fieldset className="radio-group">
                  <legend>{t('oauth.household')}</legend>
                  {writable.map((h) => (
                    <label key={h.id} className="checkbox">
                      <input type="radio" name={groupHousehold} checked={chosen === h.id} onChange={() => setHouseholdId(h.id)} />
                      <span>{h.name}</span>
                    </label>
                  ))}
                </fieldset>
              )}
              {writable.length === 1 && <p className="muted">{t('oauth.oneHousehold', { household: writable[0]!.name })}</p>}
              <fieldset className="radio-group">
                <legend>{t('oauth.access')}</legend>
                {(['read_write', 'read'] as const).map((s) => (
                  <label key={s} className="checkbox">
                    <input type="radio" name={groupScope} checked={scope === s} onChange={() => setScope(s)} />
                    <span>
                      <strong>{t(`oauth.scopes.${s}`)}</strong>
                      <br />
                      <span className="muted">{t(`oauth.scopeHints.${s}`)}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <p className="muted">{t('oauth.revokeHint')}</p>
              <div className="row">
                <button type="button" className="btn" disabled={busy || !chosen} onClick={() => void decide('allow')}>
                  {t('oauth.allow')}
                </button>
                <button type="button" className="btn secondary" disabled={busy} onClick={() => void decide('deny')}>
                  {t('oauth.deny')}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
